import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NavigationExtras, ActivatedRoute } from '@angular/router';
import { NavController, ToastController } from '@ionic/angular';
import { KilnService } from 'src/app/services/kiln.service';
import { UserService } from 'src/app/services/user.service';
import { AccountService } from 'src/app/services/account.service';
import { environment } from '../../environments/environment';
import { StorageService } from '../services/storage.service';
import { ViewportService } from '../services/viewport.service';
import { DefaultLandingService } from '../services/default-landing.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-home',
  templateUrl: 'home.page.html',
  styleUrls: ['home.page.scss'],
})
export class HomePage implements OnInit {
  public errorText: string = null;
  public loginUsername: string;
  public loginEmail: string;
  public loginPassword: string;
  public loginRemember: boolean = false;
  public loginBtnText: string = '';
  public isLoggedIn: boolean;
  public loginData = {
    "status":"",
    "email":"",
    "token":"",
    "needs_to_accept_new_terms_and_conditions": true,
    "new_terms_and_conditions":"",
    "role": ""
  };
  public isLoggingIn = false;
  public logoUrl = "assets/kilnlink_logo_transparent.png";
  public successMessage;
  public show_premium = false;
  public hasLoginInfo = false;
  public whatIsKilnLinkOpen = false;
  public whatIsKilnLinkBullets: string[] = [];
  private redirectRoute;

  constructor(    public navCtrl: NavController,
    private viewport: ViewportService,
    private defaultLanding: DefaultLandingService,
    public userService: UserService,
    public kilnService: KilnService,
    public accountService: AccountService,
    private storageService: StorageService,
    private route: ActivatedRoute,
    private toastCtrl: ToastController,
    private elementRef: ElementRef,
    private translate: TranslateService
  ) {
    let page = this;
    void this.storageService.ready().then(() => {
      page.storageService.get('rememberMe').then(function(val) {
        page.loginRemember = val ? val : false;
      });
    });

    this.userService.storageUpdate.subscribe((data) => {
      if(data && data['email']) { this.onStorageEmail(data['email']) }
      if(data && data['password']) { this.onStoragePassword(data['password']) }
    })

    // this.events.subscribe('needsLoginAgain', (data) => {
    //   this.showSuccessMessage("Your session has expired, please log in again");
    //   this.onLogoutSuccess(data);
    // })

    this.route.queryParams.subscribe(params => {
      this.successMessage = params['successMessage'];
      if(this.successMessage) {
        this.showSuccessMessage(this.successMessage)
      }
    });
    this.show_premium = environment.show_premium;
    if(this.show_premium) {
      document.body.classList.toggle('premium', true);
    }

    // Set the login button text using translation
    this.translate.get('HOME.SIGN_IN').subscribe((res: string) => {
      this.loginBtnText = res;
    });
  }

  ngOnInit() {
    this.userService.getStoredEmail();
    this.userService.getStoredPassword();
    this.kilnService.startTimerSecond(); //start the second timer which will always run
    const page = this;

    void this.storageService.ready().then(() => {
      page.storageService.get('email').then(function(val) {
        page.loginEmail = val;
        page.storageService.get('password').then(function(passwordVal) {
          page.loginPassword = passwordVal;
          page.storageService.get('rememberMe').then(function(rememberMeVal) {
            page.loginRemember = rememberMeVal ? rememberMeVal : false;
            if(page.loginRemember) {
              if(page.loginEmail && page.loginPassword) {
                page.hasLoginInfo = true;
                if (!page.userService.LoggedIn() && !page.userService.isLoginInFlight()) {
                  page.isLoggingIn = true;
                  page.storageService.set('rememberMe', page.loginRemember);
                  page.userService.tryLogin(page.loginEmail, page.loginPassword)
                      .subscribe(
                          function(response) { page.onLoginSuccess(response)},
                          function(error) { page.onLoginError(error) })
                }
              }
            } else {
              page.storageService.remove('rememberMe');
            }
          });
        });
      });
    });
  }

  public ngOnDestroy () {
    this.kilnService.killTimerSecond(); //stop the second timer at close
    this.userService.killTimerSecond();
    this.isLoggingIn = false;
  }

  public ngAfterViewInit(){
    if (this.userService.LoggedIn()){
      this.loginBtnText = 'Logout';
      this.isLoggedIn = true;
    } else {
      this.loginBtnText = 'Sign In';
      this.isLoggedIn = false;
      this.isLoggingIn = false;
    }
  }

  async showSuccessMessage(message) {
    let toast = await this.toastCtrl.create({
      message: message || this.successMessage,
      duration: 3000,
      position: 'middle',
      cssClass: 'error'
    });

    toast.onDidDismiss().then(() => {
      this.successMessage = null;
    });

    toast.present();
  }

  public onStorageEmail(email){
    if (email){
      this.loginEmail = email
    };
  }

  public onStoragePassword(password){
    if (password){
      this.loginPassword = password
    };
  }

  public onRememberMeChange(event: any) {
    this.loginRemember = event.detail.checked;
    this.storageService.set('rememberMe', this.loginRemember);
    
    // If remember me is checked and user has entered credentials, save them and try auto-login
    if (this.loginRemember && this.loginEmail && this.loginPassword) {
      this.userService.setStoredUserData(this.loginEmail, this.loginPassword);
      
      // Only attempt auto-login if not already logged in and not currently logging in
      if (!this.userService.LoggedIn() && !this.isLoggingIn && !this.userService.isLoginInFlight()) {
        this.isLoggingIn = true;
        this.userService.tryLogin(this.loginEmail, this.loginPassword)
          .subscribe(
            (response) => { this.onLoginSuccess(response); },
            (error) => { this.onLoginError(error); }
          );
      }
    } else if (!this.loginRemember) {
      // If remember me is unchecked, remove stored credentials
      this.userService.removeStoredUserData();
    }
  }

  public loginClick() {
    if (this.isLoggingIn || this.userService.isLoginInFlight()) {
      return;
    }
    if (this.loginRemember) {
      //store the user info if "remember me" is checked
      this.userService.setStoredUserData(this.loginEmail, this.loginPassword);
    } else {
      //remove the stored user info if "remember me" is not checked
      this.userService.removeStoredUserData();
    }
    if (!this.userService.LoggedIn()) {
      this.isLoggingIn = true;
      this.storageService.set('rememberMe', this.loginRemember);
      this.userService.tryLogin(this.loginEmail, this.loginPassword)
          .subscribe((response) => { this.onLoginSuccess(response); },
              (error) => { this.onLoginError(error) })
    } else {
      this.isLoggingIn = false;
      this.userService.tryLogOut(this.loginData.token).then((response) => { this.onLoginSuccess(response)} ).catch((error) => { this.onLoginError(error)})
    }
  }

  cancelLogin() {
    this.isLoggingIn = false;
  }

  /**
   * Phones should not follow desktop deep redirects after auth. An explicit
   * default home (Quick Actions / Kiln List / Dashboard) is still honored.
   */
  private postAuthRoute(
    route: { path: string; fragment?: string },
    hadRedirect = false
  ): { path: string; fragment?: string } {
    if (!this.viewport.shouldUseKilnListRoute() && hadRedirect) {
      return { path: 'landing', fragment: route?.fragment };
    }
    return route;
  }

  private defaultHomeRoute(): { path: string } {
    return { path: this.defaultLanding.getHomePath() };
  }

  private needsPasswordChanged(response) {
    this.loginData = response;
    this.isLoggedIn = true;
    this.isLoggingIn = false;
    this.userService.setPendingNewPassword({
      email: this.loginEmail,
      old_password: this.loginPassword,
      userAttributes: response.userAttributes,
    });
    this.navCtrl.navigateForward('new-password', {
      queryParams: { email: this.loginEmail },
    });
  }

  private onLoginSuccess(response) {
    if (response.newPasswordRequired) {
      this.needsPasswordChanged(response);
      return;
    }

    if(response.account && this.accountService) {      
      this.accountService.setCurrent(response.account);
      // if(response.account === '0' || response.account === 0 && response.kiln_info && response.kiln_info.length === 0) { this.kilnService.getKilnData()}
    }
    
    this.loginData = response;
    this.isLoggedIn = true;
    this.isLoggingIn = false;
    this.errorText = null;
    this.successMessage = null;
    // Server may send either needsToAgreeToTermsAgain (from client logic) or needs_terms_accepted (from API).
    if (response.needsToAgreeToTermsAgain || (response as any).needs_terms_accepted) {
      this.loginData.needs_to_accept_new_terms_and_conditions = true;
      this.loginData.new_terms_and_conditions = this.loginData.new_terms_and_conditions || (response as any).terms_endpoint || '';
    }
    if (this.loginData.needs_to_accept_new_terms_and_conditions){
      let navigationExtras: NavigationExtras = {
          queryParams: {
            email: this.loginData.email,
            token: this.loginData.token,
            newTerms: this.loginData.new_terms_and_conditions
          }
      };
      this.navCtrl.navigateForward('/accept-terms', navigationExtras);
    } else {
      this.kilnService.setTimerSecond(1);
      this.userService.setTimerSecond(1);
      this.userService.loadAllKilnInfo();

      if(["super-admin", "premium-user"].includes(this.loginData.role) && this.show_premium) {
        document.body.classList.toggle('premium', true);
      }

      const hadRedirect = !!this.userService.getRedirect();
      let nextRoute = hadRedirect ? this.userService.getRedirect() : this.defaultHomeRoute();
      this.userService.clearRedirect();
      nextRoute = this.postAuthRoute(nextRoute, hadRedirect);
      let navigationExtras: NavigationExtras = {
          queryParams: {
            token: this.loginData.token,
            email: this.loginData.email,
            role: this.loginData.role,
          }, fragment: nextRoute.fragment
      };
      this.navCtrl.navigateForward(nextRoute.path, navigationExtras);
    }
  }

  private onLoginError(errorResponse) {
    console.log(`onLoginError - ${JSON.stringify(errorResponse)}`)
    this.isLoggingIn = false;
    const body = errorResponse?.error || errorResponse || {};
    if (body.password_attempts_exceeded) {
      this.errorText = body.message || this.translate.instant('HOME.LOGIN_LOCKOUT');
    } else if (body.password_reset_required) {
      this.errorText = body.message || this.translate.instant('HOME.PASSWORD_RESET_REQUIRED');
    } else if (body.needs_confirmation) {
      this.errorText = body.message || this.translate.instant('HOME.NEEDS_CONFIRMATION');
    } else {
      this.errorText = body.message || this.translate.instant('HOME.LOGIN_ERROR');
    }
  }

  private onLogoutSuccess (response) {
    this.successMessage = null;
    if(!this.loginRemember) {
      this.loginEmail = '';
      this.loginPassword = '';
    }

    this.userService.removeStoredUserData();
    this.loginBtnText = 'Sign In';
    this.isLoggedIn = false;
    this.isLoggingIn = false;
    this.loginData.token = '';
  }

  // private onLogoutError (error){
  //   this.errorText = 'Logout error! ' + error;
  // }

  public toKilnList() {
    if (this.loginData.needs_to_accept_new_terms_and_conditions){
      let navigationExtras: NavigationExtras = {
          queryParams: {
            token: this.loginData.token,
            newTerms: this.loginData.new_terms_and_conditions
          }
      };
      this.navCtrl.navigateForward('/accept-terms', navigationExtras);
    } else {
      const hadRedirect = !!this.userService.getRedirect();
      let nextRoute = hadRedirect ? this.userService.getRedirect() : this.defaultHomeRoute();
      this.userService.clearRedirect();
      nextRoute = this.postAuthRoute(nextRoute, hadRedirect);

      let navigationExtras: NavigationExtras = {
        queryParams: {
          token: this.loginData.token,
          email: this.loginData.email,
          role: this.loginData.role
        }, fragment: nextRoute.fragment
      };

      this.navCtrl.navigateForward(nextRoute.path, navigationExtras);
    }
  }

  public createUser(){
    this.navCtrl.navigateForward('new-user');
  }

  public forgotPassword() {
    this.navCtrl.navigateForward('forgot-password');
  }

  public recoverEmail() {
    void this.navCtrl.navigateForward('recover-email');
  }

  public confirmAccount(): void {
    const email = (this.loginEmail || '').trim();
    const navigationExtras: NavigationExtras = {
      queryParams: email ? { email } : {},
    };
    this.navCtrl.navigateForward('/confirm-user', navigationExtras);
  }

  presentWhatIsKilnLink(): void {
    this.whatIsKilnLinkBullets = Array.from({ length: 9 }, (_, i) =>
      this.translate.instant(`LANDING.WHAT_IS_KILNLINK_BULLET_${i + 1}`)
    );
    this.whatIsKilnLinkOpen = true;
  }

  closeWhatIsKilnLink(): void {
    this.whatIsKilnLinkOpen = false;
  }

  public focusInput(input) {
    var el = this.elementRef.nativeElement.querySelector(`ion-input[id="${input}"] input`);
    el && el.focus();
  }
}
